import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Users, Globe, Award } from 'lucide-react';
import { getPartnersPageData, formatPartnersPageData } from '../api/strapi/partnersPageApi';
import SectionGuard from '../components/SectionGuard';
import DataLoader from '../components/loader/DataLoader';
import PageHero from '../components/common/PageHero';
import SkeletonImage from '../components/common/SkeletonImage';

const PartnersPage = () => {
  const intl = useIntl();
  const [pageData, setPageData] = useState(null);
  const [pageResult, setPageResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartnersData = async () => {
      try {
        setLoading(true);
        const result = await getPartnersPageData();
        setPageResult(result);

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            console.warn("Partners page data unavailable:", result);
          }
          setPageData(null);
          return;
        }

        const formattedData = formatPartnersPageData(result.data);
        setPageData(formattedData);
      } catch (error) {
        console.error('Error fetching partners page data:', error);
        setPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnersData();
  }, []);

  if (loading) {
    return <DataLoader isLoading={true} />;
  }

  const sectionFallback = (
    <div className="py-10 text-center text-gray-500 text-sm">
      {intl.formatMessage({ id: "empty.section" })}
    </div>
  );

  // Map icon names to actual icon components
  const getIconComponent = (iconName) => {
    switch(iconName?.toLowerCase()) {
      case 'building':
        return Building2;
      case 'users':
        return Users;
      case 'globe':
        return Globe;
      case 'award':
        return Award;
      default:
        return Building2; // Default icon
    }
  };

  // Get color value for category
  const getCategoryColor = (colorName) => {
    const colorMap = {
      'blue': '#3b82f6',
      'green': '#10b981',
      'purple': '#8b5cf6',
      'yellow': '#f59e0b',
      'orange': '#f97316',
      'red': '#ef4444',
      'gray': '#6b7280'
    };

    return colorMap[colorName] || '#6b7280'; // Default to gray if color not found
  };

  const categories = pageData?.categories || [];
  const partners = pageData?.partners || [];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageData?.title ? `${pageData.title} | Africa Systems` : intl.formatMessage({ id: "partners.meta.title" })}</title>
        <meta name="description" content={pageData?.metaDescription || intl.formatMessage({ id: "partners.meta.description" })} />
      </Helmet>

      {/* Hero Section */}
      <SectionGuard result={pageResult} fallback={sectionFallback} logLabel="Partners hero">
        <PageHero
          eyebrow="Africa Systems"
          title={pageData?.heroComponent?.title}
          description={pageData?.heroComponent?.description}
          image={pageData?.heroComponent?.image || 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80'}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
              {intl.formatMessage({ id: "partners.cta.become" })}
            </button>
            <button className="border border-white/40 text-white hover:bg-white hover:text-[#0a1628] px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
              {intl.formatMessage({ id: "partners.cta.contactSales" })}
            </button>
          </motion.div>
        </PageHero>
      </SectionGuard>

      {/* Stats Section */}
      <SectionGuard result={pageResult} fallback={null} logLabel="Partners stats">
        <section className="py-16 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {pageData?.stats?.map((stat, index) => {
                const IconComponent = getIconComponent(stat.label);
                return (
                  <motion.div
                    key={stat.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#16A34A]/10 rounded-full mb-4">
                      <IconComponent className="w-8 h-8 text-[#16A34A]" />
                    </div>
                    <div className="text-3xl font-bold text-[#0a1628] mb-2">{stat.value}</div>
                    <div className="text-slate-600">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </SectionGuard>

      {/* Categories Section */}
      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          className="text-3xl md:text-4xl font-bold text-center text-[#0a1628] mb-12"
          >
            {intl.formatMessage({ id: "partners.categories.title" })}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              // Compter le nombre de partenaires par catégorie
              const categoryCounts = {};
              partners.forEach(partner => {
                const categoryName = partner.category?.name;
                if (categoryName) {
                  if (categoryCounts[categoryName]) {
                    categoryCounts[categoryName]++;
                  } else {
                    categoryCounts[categoryName] = 1;
                  }
                }
              });

              // Créer un tableau de catégories avec les comptages
              const categoriesWithCounts = Object.entries(categoryCounts).map(([name, count]) => {
                // Trouver les détails de la catégorie dans pageData.categories
                const categoryDetails = categories.find(cat => cat.name === name) || {};

                return {
                  name: name,
                  count: count,
                  color: categoryDetails.color || 'gray',
                  id: categoryDetails.id
                };
              });

              return categoriesWithCounts.map((category, index) => (
                <motion.div
                  key={category.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 hover:border-[#16A34A]/40"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-[#0a1628] mb-2">{category.name}</h3>
                      <p className="text-slate-600">
                        {intl.formatMessage({ id: "partners.categories.count" }, { count: category.count })}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center`} style={{backgroundColor: getCategoryColor(category.color)}}>
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </motion.div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          className="text-3xl md:text-4xl font-bold text-center text-[#0a1628] mb-12"
          >
            {intl.formatMessage({ id: "partners.grid.title" })}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col border border-slate-200 hover:border-[#16A34A]/40"
              >
                <div className="p-6 flex-grow">
                  <div className="flex items-center mb-4">
                    <SkeletonImage
                      src={partner.logo}
                      alt={partner.name}
                      className="w-16 h-8 mr-4"
                      imgClassName="w-full h-full object-contain"
                      icon={false}
                    />
                    <div>
                      <h3 className="text-xl font-bold text-[#0a1628]">{partner.name}</h3>
                      <span className="inline-block bg-[#16A34A]/10 text-[#15803D] text-xs px-2 py-1 rounded-full mt-1">
                        {partner.category?.name}
                      </span>
                    </div>
                  </div>

                  <p className="text-slate-600 mb-4">{partner.description}</p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-[#0a1628] mb-2">{intl.formatMessage({ id: "partners.services.label" })}</h4>
                    <div className="flex flex-wrap gap-2">
                      {partner.services.map((service, idx) => (
                        <span
                          key={service.id || idx}
                          className="bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded"
                        >
                          {service.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mb-4">
                    <div className="flex items-center">
                      <Globe className="w-4 h-4 mr-2" />
                      {partner.location}
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-2" />
                      {partner.employees}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {partner.certifications.map((cert, idx) => (
                      <span
                        key={cert.id || idx}
                        className="bg-[#16A34A]/10 text-[#15803D] text-xs px-2 py-1 rounded"
                      >
                        {cert.name}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-neutral-50 px-6 py-4 border-t border-slate-100 mt-auto">
                  {partner.url ? (
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full block text-center text-[#16A34A] font-semibold hover:text-[#15803D] transition-colors duration-200"
                    >
                      {intl.formatMessage({ id: "partners.visitWebsite" })} <ArrowRight className="inline w-4 h-4 ml-1" />
                    </a>
                  ) : (
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full block text-center text-[#16A34A] font-semibold hover:text-[#15803D] transition-colors duration-200"
                    >
                      {intl.formatMessage({ id: "partners.visitWebsite" })} <ArrowRight className="inline w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <SectionGuard result={pageResult} fallback={null} logLabel="Partners CTA">
        <section className="py-20 bg-[#0a1628]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-6"
          >
            {pageData?.ctaSection?.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-white/75 max-w-2xl mx-auto mb-8"
          >
            {pageData?.ctaSection?.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <button className="bg-[#16A34A] hover:bg-[#15803D] text-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
              {intl.formatMessage({ id: "partners.cta.apply" })}
            </button>
            <button className="border border-white/40 text-white hover:bg-white hover:text-[#0a1628] px-6 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors">
              {intl.formatMessage({ id: "partners.cta.learnMore" })}
            </button>
          </motion.div>
        </div>
        </section>
      </SectionGuard>
    </div>
  );
};

export default PartnersPage;
